// ─── Sprint 043 — Operator Activation Wizard™ ────────────────────────────────
// Operator profile collected during the Activation Wizard and persisted
// to localStorage. This is the authoritative source for operator preferences.

// ─── Step 4: Opportunity category constants ───────────────────────────────────

/** All selectable opportunity interest categories. Display order preserved. */
export const OPPORTUNITY_CATEGORIES = [
  'AI Training',
  'Surveys',
  'Usability Testing',
  'Micro Tasks',
  'Search Evaluation',
  'Data Collection',
  'Annotation',
  'Research Studies',
  'Transcription',
  'Other',
] as const;

export type OpportunityCategory = typeof OPPORTUNITY_CATEGORIES[number];

// ─── Operator profile ─────────────────────────────────────────────────────────

export interface OperatorProfile {
  // ── Step 2: Income Goal ─────────────────────────────────────────────────────
  /** Operator's target daily income in USD. Default: 25. */
  dailyIncomeGoal: number;
  /** Derived: dailyIncomeGoal × 7 */
  weeklyIncomeGoal: number;
  /** Derived: dailyIncomeGoal × 30 — stored in settings as incomeGoal */
  monthlyIncomeGoal: number;

  // ── Step 3: Work Preferences ─────────────────────────────────────────────────
  /** Operator's country of residence (free text). */
  country: string;
  /** Hours the operator can dedicate per day. */
  availableHoursPerDay: number;
  /** Whether mobile-compatible opportunities are required. */
  mobileCompatibleRequired: boolean;
  /** Whether desktop-only opportunities are acceptable. */
  desktopAllowed: boolean;
  /** Whether the operator has a resume ready to submit. */
  resumeAvailable: boolean;
  /** Whether the operator has a cover letter ready to submit. */
  coverLetterAvailable: boolean;

  // ── Step 4: Opportunity Interests ────────────────────────────────────────────
  /** Selected opportunity interest categories. Minimum 1 recommended. */
  opportunityCategories: OpportunityCategory[];

  // ── Step 5: Acquisition Preferences ─────────────────────────────────────────
  /**
   * IDs of acquisition methods the operator has enabled.
   * Matches AcquisitionMethod.id from acquisitionStrategyService.
   */
  enabledAcquisitionMethodIds: string[];

  // ── Metadata ──────────────────────────────────────────────────────────────────
  /** ISO timestamp when activation was completed. */
  activatedAt: string;
  /** Schema version for future migrations. */
  profileVersion: '1';
}

// ─── Default profile ──────────────────────────────────────────────────────────
// Applied as the starting state for the wizard form.

export const DEFAULT_PROFILE: Omit<OperatorProfile, 'activatedAt'> = {
  dailyIncomeGoal:           25,
  weeklyIncomeGoal:          175,
  monthlyIncomeGoal:         750,
  country:                   '',
  availableHoursPerDay:      4,
  mobileCompatibleRequired:  true,
  desktopAllowed:            true,
  resumeAvailable:           false,
  coverLetterAvailable:      false,
  opportunityCategories:     [],
  enabledAcquisitionMethodIds: [
    'manual-entry',
    'saved-urls',
    'rss-feeds',
    'public-job-boards',
    'csv-import',
    'browser-share',
  ],
  profileVersion: '1',
};
